import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  User, ArrowLeft, Download, Mail, Phone, MapPin, GraduationCap, BookOpen,
  Award, UserCheck, FolderOpen, ExternalLink, Briefcase, ShieldCheck, ShieldX, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { SendMessageButton } from '../components/MessagesPanel';
import { timeAgo, freshness } from '../lib/timeAgo';
import { sortByLatest } from '../lib/sortByLatest';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHART_COLORS = ['#01354d', '#c89c3a', '#0e7490', '#059669', '#7c3aed', '#dc2626'];

const portfolioPlatformNames = {
  github: 'GitHub', linkedin: 'LinkedIn', youtube: 'YouTube', behance: 'Behance',
  dribbble: 'Dribbble', orcid: 'ORCID', google_drive: 'Google Drive', portfolio: 'Website',
};

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ensureAbsoluteUrl(url) {
  if (!url) return url;
  return url.match(/^https?:\/\//i) ? url : `https://${url}`;
}

export default function CandidateFullView() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [referenceStatuses, setReferenceStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/candidates/${candidateId}`, { headers: getAuthHeaders() });
        let skills = res.data.skills || [];
        if (skills.length > 0 && typeof skills[0] === 'string') {
          skills = skills.map(s => ({ name: s, reference: {} }));
        }
        setCandidate({ ...res.data, skills, experience: res.data.experience || [] });
        // Load verification statuses
        try {
          const statusRes = await axios.get(`${API}/references/statuses/${candidateId}`, { headers: getAuthHeaders() });
          setReferenceStatuses(statusRes.data || []);
        } catch (e) { /* non-critical */ }
      } catch (err) {
        console.error('Failed to load candidate:', err);
        alert('Failed to load candidate profile.');
        navigate('/organization-dashboard');
      } finally { setLoading(false); }
    };
    load();
  }, [candidateId, navigate]);

  const handleDownloadPDF = () => {
    if (!candidate) return;
    const c = candidate;
    const eduSorted = sortByLatest(c.education || [], 'end_year', 'start_year');
    const expSorted = sortByLatest(c.experience || [], 'end_date', 'start_date');
    const eduHtml = eduSorted.length > 0 ? `<div class="s"><div class="st">Education / Training</div>${eduSorted.map(e => `<div class="cd" style="border-left-color:${e.type === 'tertiary' ? '#01354d' : '#c89c3a'}"><strong>${e.institution || ''}</strong>${e.qualification ? `<br>${e.qualification}${e.field_of_study ? ` in ${e.field_of_study}` : ''}` : ''}${e.start_year ? `<br><small>${e.start_year}${e.end_year ? ` - ${e.end_year}` : ''}</small>` : ''}</div>`).join('')}</div>` : '';
    const expHtml = expSorted.length > 0 ? `<div class="s"><div class="st">Experience</div>${expSorted.map(e => `<div class="cd" style="border-left-color:#dc2626"><strong>${e.role || ''}</strong>${e.company ? `<br>${e.company}` : ''}${e.location ? ` - ${e.location}` : ''}${e.start_date ? `<br><small>${e.start_date}${e.is_current ? ' - Present' : (e.end_date ? ` - ${e.end_date}` : '')}</small>` : ''}${e.responsibilities ? `<br><small>${e.responsibilities}</small>` : ''}</div>`).join('')}</div>` : '';
    const skillsHtml = c.skills.length > 0 ? `<div class="s"><div class="st">Skills & References</div>${c.skills.map(s => `<div class="cd" style="border-left-color:#c89c3a"><strong>${s.name}</strong>${s.reference?.name ? `<br><small>Ref: ${s.reference.name} ${s.reference.surname || ''} - ${s.reference.title || ''} at ${s.reference.organisation || ''}</small>` : ''}${s.reference?.email ? `<br><small>${s.reference.email}${s.reference.phone ? ` | ${s.reference.phone}` : ''}</small>` : ''}</div>`).join('')}</div>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${c.name} - MeritumPraxis</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',sans-serif;padding:40px;max-width:800px;margin:0 auto;color:#062240}.db{position:fixed;top:0;left:0;right:0;background:#01354d;color:white;padding:12px 20px;display:flex;justify-content:space-between;align-items:center;z-index:1000}.btn{background:#c89c3a;color:#01354d;border:none;padding:8px 20px;border-radius:4px;cursor:pointer;font-weight:bold}.ct{margin-top:60px}.hd{text-align:center;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #c89c3a}.pp{width:120px;height:120px;border-radius:50%;margin:0 auto 20px;display:block;object-fit:cover;border:3px solid #c89c3a}.s{margin-bottom:25px}.st{font-size:18px;font-weight:bold;color:#01354d;margin-bottom:10px;padding-bottom:5px;border-bottom:2px solid #01354d}.cd{margin-bottom:10px;padding:10px;background:#f8f9fa;border-left:3px solid #01354d}.ft{margin-top:40px;padding-top:20px;border-top:2px solid #ddd;text-align:center;font-size:12px;color:#666}@media print{.db{display:none!important}.ct{margin-top:0}body{padding:20px}}</style></head><body><div class="db"><span>${c.name} - MeritumPraxis</span><button class="btn" onclick="window.print()">Download PDF</button></div><div class="ct"><div class="hd"><div style="font-size:24px;font-weight:bold;color:#01354d">MeritumPraxis</div><div style="font-size:12px;color:#666">Progress Made Visible</div></div>${c.profile_picture ? `<img src="${c.profile_picture}" class="pp">` : ''}<div style="font-size:28px;font-weight:bold;color:#01354d;text-align:center">${c.name}</div>${c.headline ? `<div style="font-size:16px;color:#c89c3a;text-align:center;margin-top:5px">${c.headline}</div>` : ''}${(c.city || c.country) ? `<div style="font-size:14px;color:#666;text-align:center;margin-top:5px">${c.city || ''}${c.city && c.country ? ', ' : ''}${c.country || ''}</div>` : ''}<div class="s"><div class="st">Contact</div><p><strong>Email:</strong> ${c.email}</p>${c.gender ? `<p><strong>Gender:</strong> ${c.gender}</p>` : ''}</div>${c.bio ? `<div class="s"><div class="st">About</div><p style="line-height:1.6">${c.bio}</p></div>` : ''}${eduHtml}${expHtml}${skillsHtml}${c.portfolio_links && Object.entries(c.portfolio_links).filter(([,v])=>v).length ? `<div class="s"><div class="st">Portfolio</div>${Object.entries(c.portfolio_links).filter(([,v])=>v).map(([k,v])=>`<div class="cd" style="border-left-color:#0e7490"><strong>${portfolioPlatformNames[k]||k}:</strong> ${v}</div>`).join('')}</div>` : ''}<div class="ft"><p>MeritumPraxis - Progress Made Visible</p></div></div></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 pt-24"><p>Loading candidate profile...</p></div>;
  if (!candidate) return null;

  const skills = candidate.skills || [];
  const education = sortByLatest(candidate.education || [], 'end_year', 'start_year');
  const experience = sortByLatest(candidate.experience || [], 'end_date', 'start_date');
  const portfolioLinks = Object.entries(candidate.portfolio_links || {}).filter(([,v]) => v);

  const getStatus = (skillName) => {
    const inv = referenceStatuses.find(r => r.skill_name === skillName);
    return inv ? inv.status : null;
  };

  const verifiedCount = skills.filter(s => getStatus(s.name) === 'confirmed').length;

  return (
    <div data-testid="candidate-full-view" className="min-h-screen bg-slate-100 pt-24">
      {/* Top Bar */}
      <div className="bg-meritum-navy text-white py-5">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/organization-dashboard')} className="text-slate-300 hover:text-white" data-testid="back-btn"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-bold">{candidate.name}</h1>
              <p className="font-body text-slate-300 text-xs">Candidate Profile</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleDownloadPDF} variant="outline" size="sm" className="border-meritum-gold text-meritum-gold hover:bg-meritum-gold hover:text-meritum-navy font-body" data-testid="download-pdf-btn"><Download size={14} className="mr-1" />PDF</Button>
            <a href={`mailto:${candidate.email}`}><Button variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-meritum-navy font-body" data-testid="email-btn"><Mail size={14} className="mr-1" />Email</Button></a>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100 text-center">
              <div className="w-28 h-28 rounded-full bg-meritum-teal/10 flex items-center justify-center mx-auto overflow-hidden border-4 border-meritum-gold/30 mb-4">
                {candidate.profile_picture ? <img src={candidate.profile_picture} alt="" className="w-full h-full object-cover" /> : <User size={40} className="text-meritum-teal" />}
              </div>
              <h2 className="font-heading text-lg font-semibold text-meritum-navy">{candidate.name}</h2>
              {candidate.headline && <p className="font-body text-sm text-meritum-teal font-medium mt-1">{candidate.headline}</p>}
              {(candidate.city || candidate.country) && <p className="font-body text-xs text-slate-400 mt-1 flex items-center justify-center gap-1"><MapPin size={12} />{candidate.city}{candidate.city && candidate.country ? ', ' : ''}{candidate.country}</p>}
              {candidate.gender && <p className="font-body text-xs text-slate-400">{candidate.gender}</p>}
              <div className="flex items-center justify-center gap-1 mt-2"><Mail size={12} className="text-slate-400" /><a href={`mailto:${candidate.email}`} className="font-body text-xs text-meritum-teal hover:underline">{candidate.email}</a></div>
              {verifiedCount > 0 && (
                <div className="mt-3 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100" data-testid="verification-summary">
                  <div className="flex items-center justify-center gap-2">
                    <ShieldCheck size={16} className="text-emerald-600" />
                    <span className="font-body text-xs font-medium text-emerald-700">{verifiedCount}/{skills.length} skills verified</span>
                  </div>
                </div>
              )}
              {candidate.updated_at && (
                <div
                  className={`mt-3 inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full font-body text-[10px] ${
                    freshness(candidate.updated_at) === 'fresh' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                    freshness(candidate.updated_at) === 'stale' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                    'bg-slate-100 text-slate-500 border border-slate-200'
                  }`}
                  data-testid="candidate-last-updated"
                >
                  <Clock size={10} />Profile updated {timeAgo(candidate.updated_at)}
                </div>
              )}
              <div className="mt-4">
                <SendMessageButton recipientId={candidate.id} recipientName={candidate.name} className="w-full" />
              </div>
            </div>

            {skills.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
                <h4 className="font-heading text-sm font-semibold text-meritum-navy mb-3">Skills Overview</h4>
                <div className="w-full h-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={skills.map((s, i) => ({ name: s.name || `Skill ${i+1}`, value: 1 }))} dataKey="value" cx="50%" cy="50%" outerRadius={45} strokeWidth={1} stroke="#fff">
                        {skills.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(_, name) => [name, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {skills.map((s, i) => (
                    <span key={i} className="flex items-center gap-1 text-[10px] font-body text-slate-600">
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Bio */}
            {candidate.bio && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <h3 className="font-heading text-base font-semibold text-meritum-navy mb-3 flex items-center gap-2"><User size={18} className="text-blue-600" />About</h3>
                <p className="font-body text-sm text-slate-600 whitespace-pre-wrap">{candidate.bio}</p>
              </div>
            )}

            {/* Education */}
            {education.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <h3 className="font-heading text-base font-semibold text-meritum-navy mb-3 flex items-center gap-2"><GraduationCap size={18} className="text-emerald-600" />Education / Training</h3>
                <div className="space-y-3">
                  {education.map((edu, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${edu.type === 'tertiary' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {edu.type === 'tertiary' ? <GraduationCap size={14} className="text-emerald-600" /> : <BookOpen size={14} className="text-amber-600" />}
                        <span className="font-body text-[10px] font-medium uppercase tracking-wide text-slate-400">{edu.type === 'tertiary' ? 'Tertiary' : 'High School'}</span>
                      </div>
                      <p className="font-heading font-semibold text-meritum-navy">{edu.institution}</p>
                      {(edu.qualification || edu.field_of_study) && <p className="font-body text-sm text-slate-600">{edu.qualification}{edu.qualification && edu.field_of_study ? ' in ' : ''}{edu.field_of_study}</p>}
                      {(edu.start_year || edu.end_year) && <p className="font-body text-xs text-slate-400">{edu.start_year}{edu.start_year && edu.end_year ? ' - ' : ''}{edu.end_year}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Experience */}
            {experience.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <h3 className="font-heading text-base font-semibold text-meritum-navy mb-3 flex items-center gap-2"><Briefcase size={18} className="text-rose-600" />Experience</h3>
                <div className="space-y-3">
                  {experience.map((exp, i) => (
                    <div key={i} className="p-4 rounded-lg border border-rose-200 bg-rose-50/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Briefcase size={14} className="text-rose-600" />
                        <span className="font-body text-[10px] font-medium uppercase tracking-wide text-slate-400">{exp.is_current ? 'Current' : 'Previous'}</span>
                      </div>
                      <p className="font-heading font-semibold text-meritum-navy">{exp.role}</p>
                      {exp.company && <p className="font-body text-sm text-meritum-teal font-medium">{exp.company}{exp.location ? ` - ${exp.location}` : ''}</p>}
                      {exp.start_date && <p className="font-body text-xs text-slate-400">{exp.start_date}{exp.is_current ? ' - Present' : (exp.end_date ? ` - ${exp.end_date}` : '')}</p>}
                      {exp.responsibilities && <p className="font-body text-sm text-slate-600 mt-2 whitespace-pre-wrap">{exp.responsibilities}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills & References */}
            {skills.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <h3 className="font-heading text-base font-semibold text-meritum-navy mb-3 flex items-center gap-2"><Award size={18} className="text-amber-600" />Skills & References</h3>
                <div className="space-y-3">
                  {skills.map((s, i) => {
                    const status = getStatus(s.name);
                    return (
                    <div key={i} className={`border rounded-lg p-4 ${status === 'confirmed' ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-3 h-3 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <h4 className="font-heading font-semibold text-meritum-navy">{s.name}</h4>
                        {status === 'confirmed' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-body" data-testid={`fv-verified-${i}`}><ShieldCheck size={12} />Verified</span>}
                        {status === 'pending' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-body"><Clock size={12} />Pending</span>}
                        {status === 'declined' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-body"><ShieldX size={12} />Declined</span>}
                      </div>
                      {s.reference?.name && (
                        <div className="ml-5 p-3 bg-slate-50 rounded-lg border border-slate-100">
                          <p className="font-body text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Reference</p>
                          <p className="font-body text-sm text-meritum-navy font-medium">{s.reference.name} {s.reference.surname}</p>
                          {s.reference.title && <p className="font-body text-sm text-meritum-teal">{s.reference.title}{s.reference.organisation ? ` at ${s.reference.organisation}` : ''}</p>}
                          <div className="flex flex-wrap gap-3 mt-1">
                            {s.reference.email && <span className="font-body text-xs text-slate-500 flex items-center gap-1"><Mail size={10} />{s.reference.email}</span>}
                            {s.reference.phone && <span className="font-body text-xs text-slate-500 flex items-center gap-1"><Phone size={10} />{s.reference.phone}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Portfolio */}
            {portfolioLinks.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                <h3 className="font-heading text-base font-semibold text-meritum-navy mb-3 flex items-center gap-2"><FolderOpen size={18} className="text-violet-600" />Portfolio Links</h3>
                <div className="space-y-2">
                  {portfolioLinks.map(([k, v]) => (
                    <a key={k} href={ensureAbsoluteUrl(v)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-meritum-teal hover:underline">
                      <ExternalLink size={14} /><span className="font-medium">{portfolioPlatformNames[k] || k}:</span><span className="truncate">{v}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
